import React, { useState } from 'react';
import TrashCan from '@uber/base-icons/trash-can';
import { styled, useStyletron } from 'baseui';
import { Button } from 'baseui/button';
import { KIND as TagKind, Tag } from 'baseui/tag';
import { LabelSmall } from 'baseui/typography';
import { useSetAtom } from 'jotai';
import type { RangeSpanT } from '../../../../../common/types/types';
import { SIDE_PANEL_ACTIONS } from '../../../../../modernization-common/side-panel/const';
import type { ModernizedGenericAnnotation, SidePanelDispatchAtom } from '../../../../../modernization-common/side-panel/types';
import { SidePanelAccordion } from '../../../../../modernization-common/side-panel-m4';
import type { AnnotationT } from '../../../../labelling/types';
import { useAnnotationMetadataForId } from '../../../atoms/annotation-atoms';
import type { DynamicAnswerSpanT, KeyframeEntryT } from '../../../studio-store/types';
const KEY_FRAME_TYPE = ({
  MANUAL: 'MANUAL',
  AUTOMATED: 'AUTOMATED'
} as const);
const Container = styled('div', ({
  $theme
}) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: $theme.sizing.scale600,
  marginBottom: $theme.sizing.scale600
}));
const baseButtonOverrides = {
  BaseButton: {
    style: ({
      $theme
    }) => ({
      margin: 0,
      padding: 0,
      backgroundColor: 'transparent',
      color: $theme.colors.contentPrimary
    })
  }
};
const frameTagOverrides = {
  Root: {
    style: ({
      $theme
    }) => ({
      padding: 0,
      margin: 0,
      backgroundColor: 'transparent',
      border: 'none',
      ...$theme.typography.ParagraphSmall
    })
  }
};
const dynamicFrameTagOverrides = {
  Root: {
    style: ({
      $theme
    }) => ({
      margin: 0,
      borderRadius: $theme.sizing.scale300
    })
  },
  Text: {
    style: ({
      $theme
    }) => ({
      ...$theme.typography.LabelXSmall
    })
  }
};
type DynamicAttributeRow = {
  key: string;
  frameSpan: RangeSpanT;
  label: string;
};
const getRangeKey = (rangeSpan: RangeSpanT) => `${rangeSpan.start}-${rangeSpan.end}`;
const rangesOverlap = (left: RangeSpanT, right: RangeSpanT) => left.start <= right.end && right.start <= left.end;
const getAnswerLabel = (answer: string | File) => {
  return typeof answer === 'string' ? answer : answer.name;
};
const getDynamicAttributeRowsForRange = (rangeSpan: RangeSpanT, dynamicAnswers: DynamicAnswerSpanT[] = []): DynamicAttributeRow[] => {
  return dynamicAnswers.filter(({
    frameSpan
  }) => rangesOverlap(rangeSpan, frameSpan)).flatMap(({
    frameSpan,
    answers
  }) => Object.entries(answers).flatMap(([questionId, answer]) => {
    const labels = answer.answers.map(getAnswerLabel).map(label => label.trim()).filter(Boolean);
    return labels.map((label, index) => ({
      key: `${frameSpan.start}-${frameSpan.end}-${questionId}-${index}`,
      frameSpan,
      label
    }));
  }));
};
const hasAutomatedKeyFrameInRange = (rangeSpan: RangeSpanT, keyframes: KeyframeEntryT[] = []) => {
  return keyframes.some(({
    frameIndex,
    type
  }) => rangeSpan.start <= frameIndex && frameIndex <= rangeSpan.end && type === KEY_FRAME_TYPE.AUTOMATED);
};
export const InstancesPanel = ({
  isTracked,
  annotationId,
  dispatchAtom
}: {
  isTracked: boolean;
  annotationId?: string;
  dispatchAtom: SidePanelDispatchAtom<AnnotationT & ModernizedGenericAnnotation>;
}) => {
  const [css, theme] = useStyletron();
  const dispatch = useSetAtom(dispatchAtom);
  const metadata = useAnnotationMetadataForId(annotationId ?? '');
  const rangeSpans: RangeSpanT[] = (metadata?.spans ?? []).map(({
    start,
    end
  }) => ({
    start,
    end
  }));
  const dynamicAnswers = metadata?.dynamicAnswers ?? [];
  const [userExpandedRangeKeys, setUserExpandedRangeKeys] = useState<string[]>();
  const firstRangeKey = rangeSpans[0] ? getRangeKey(rangeSpans[0]) : undefined;
  const expandedRangeKeys = userExpandedRangeKeys ?? (firstRangeKey !== undefined ? [firstRangeKey] : []);
  const handleDeleteRange = (rangeSpan: RangeSpanT) => {
    if (!annotationId) {
      return;
    }
    dispatch({
      type: SIDE_PANEL_ACTIONS.DELETE_ANNOTATION_FRAME_SPAN,
      annotationIdx: annotationId,
      frameSpan: {
        start: rangeSpan.start,
        end: rangeSpan.end
      }
    });
  };
  if (!isTracked) {
    return <div className={css({
      paddingBottom: theme.sizing.scale600
    })}>
        <LabelSmall>No instances to show</LabelSmall>
      </div>;
  }
  const sections = rangeSpans.map(rangeSpan => {
    const rangeKey = getRangeKey(rangeSpan);
    const dynamicAttributeRows = getDynamicAttributeRowsForRange(rangeSpan, dynamicAnswers);
    const instanceStatus = hasAutomatedKeyFrameInRange(rangeSpan, metadata?.keyframes) ? 'Tracked' : 'Interpolated';
    return {
      id: rangeKey,
      header: <div className={css({
        display: 'flex',
        alignItems: 'center',
        minWidth: 0
      })}>
          <div className={css({
          ...theme.typography.ParagraphSmall,
          margin: 0
        })}>
            Frame {rangeSpan.start + 1}→{rangeSpan.end + 1}&nbsp;:&nbsp;
            <Tag closeable={false} kind={TagKind.positive} overrides={{
            ...frameTagOverrides
          }}>
              {instanceStatus}
            </Tag>
          </div>
        </div>,
      endEnhancer: <Button data-testid="delete-range-span" onClick={event => {
        event.preventDefault();
        event.stopPropagation();
        handleDeleteRange(rangeSpan);
      }} overrides={{
        ...baseButtonOverrides
      }}>
          <TrashCan size={12} />
        </Button>,
      children: dynamicAttributeRows.length > 0 ? <div className={css({
        display: 'flex',
        flexDirection: 'column',
        paddingLeft: theme.sizing.scale300,
        paddingRight: theme.sizing.scale300
      })}>
            {dynamicAttributeRows.map((dynamicAttributeRow, index) => <div key={dynamicAttributeRow.key} className={css({
          display: 'flex',
          alignItems: 'center',
          gap: theme.sizing.scale300,
          paddingTop: theme.sizing.scale500,
          paddingBottom: theme.sizing.scale500,
          borderBottom: index === dynamicAttributeRows.length - 1 ? 'none' : `1px solid ${theme.colors.borderOpaque}`
        })}>
                <Tag closeable={false} kind={TagKind.accent} overrides={{
            ...dynamicFrameTagOverrides
          }}>
                  {dynamicAttributeRow.frameSpan.start + 1}→{dynamicAttributeRow.frameSpan.end + 1}
                </Tag>
                <div className={css({
            ...theme.typography.ParagraphSmall
          })}>
                  {' '}
                  · {dynamicAttributeRow.label}
                </div>
              </div>)}
          </div> : <div className={css({
        paddingTop: theme.sizing.scale400,
        paddingLeft: theme.sizing.scale300,
        paddingRight: theme.sizing.scale300
      })}>
            <LabelSmall color={theme.colors.contentTertiary} margin={0}>
              No dynamic attributes
            </LabelSmall>
          </div>
    };
  });
  return <Container>
      <SidePanelAccordion sections={sections} expandedKeys={expandedRangeKeys} onChange={expandedKeys => setUserExpandedRangeKeys(expandedKeys.map(String))} fillExpandedSection={false} fillContainer={false} overrides={{
      Root: {
        gap: theme.sizing.scale600
      },
      Section: {
        ':before': {
          display: 'none'
        }
      },
      Header: {
        backgroundColor: theme.colors.backgroundSecondary,
        borderRadius: theme.sizing.scale300,
        gap: theme.sizing.scale300,
        height: theme.sizing.scale1000,
        marginBottom: theme.sizing.scale100,
        padding: `${theme.sizing.scale100} ${theme.sizing.scale500}`
      },
      ToggleButton: {
        gap: theme.sizing.scale500
      },
      Content: {
        padding: 0
      }
    }} />
    </Container>;
};